/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RECORD_CONTACT_RECIPIENT, recordContactLabel, supportsRecordContact } from 'src/utils/workflowRecipients';
import classNames from 'classnames';
import Modal from 'src/components/layouts/Modal/Modal';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Input from 'src/components/ui/Input/Input';
import Icon from 'src/components/ui/Icon/Icon';
import Divider from 'src/components/ui/Divider/Divider';
import Select, { Option } from 'src/components/ui/Select/Select';
import TextAreaMenu from 'src/components/layouts/TextAreaMenu/TextAreaMenu';
import { useWorkflowsActionsQuery, useWorkflowsTemplatesQuery } from 'src/services/workflows';
import { usePagedUserQuery, useProfileQuery } from 'src/services/user';
import Checkbox from 'src/components/ui/Checkbox/Checkbox';
import useStockInventory, { StockItem } from 'src/services/inventory';
import { getWorkflows } from 'src/redux/workflows/selectors';
import { getActionIcon, validateEmail } from 'src/utils/general';
import styles from './ActionSelectionOverlay.module.scss';

export type ActionFormData = {
  actionType: string;
  note: string;
  // Email
  recipient?: string;
  subject?: string;
  content?: string;
  // Notification
  partnerId?: number;
  partnerName?: string;
  message?: string;
  // Case
  title?: string;
  // Product
  selectedProductId?: number;
  selectedProductName?: string;
  selectedProductBarcode?: string;
  selectedProductArticle?: string;
  amount?: number;
  unit?: string;
  referenceProductId?: number;
  referenceProductName?: string;
  referenceProductBarcode?: string;
  referenceProductArticle?: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onActionSelect: (formData: ActionFormData) => void;
  initialData?: ActionFormData | null;
};

export default function ActionSelectionOverlay({ isOpen, onClose, onActionSelect, initialData }: Props) {
  const { t } = useTranslation();

  const [selectedAction, setSelectedAction] = useState<string | null>(initialData?.actionType || null);
  const [note, setNote] = useState(initialData?.note || '');

  // Email fields
  const [recipient, setRecipient] = useState(initialData?.recipient || '');
  const [subject, setSubject] = useState(initialData?.subject || '');
  const [emailContent, setEmailContent] = useState(initialData?.content || '');

  // Notification fields
  const [selectedPartner, setSelectedPartner] = useState<{ id: number; name: string } | null>(
    initialData?.partnerId ? { id: initialData.partnerId, name: initialData.partnerName || '' } : null,
  );
  const [notificationMessage, setNotificationMessage] = useState(initialData?.message || '');

  // Case fields
  const [caseTitle, setCaseTitle] = useState(initialData?.title || '');
  const [caseMessage, setCaseMessage] = useState(initialData?.message || '');

  // Product fields
  const [selectedProduct, setSelectedProduct] = useState<StockItem | null>(null);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [amount, setAmount] = useState(initialData?.amount?.toString() || '1');
  const [selectedUnit, setSelectedUnit] = useState<Option | null>(
    initialData?.unit ? { label: '', value: initialData.unit } : null,
  );
  const [referenceProduct, setReferenceProduct] = useState<StockItem | null>(null);
  const [showReferencePicker, setShowReferencePicker] = useState(false);
  const [referenceSearchQuery, setReferenceSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen && initialData) {
      setSelectedAction(initialData.actionType || null);
      setNote(initialData.note || '');
      setRecipient(initialData.recipient || '');
      setSubject(initialData.subject || '');
      setEmailContent(initialData.content || '');
      setSelectedPartner(
        initialData.partnerId ? { id: initialData.partnerId, name: initialData.partnerName || '' } : null,
      );
      setNotificationMessage(initialData.message || '');
      setCaseTitle(initialData.title || '');
      setCaseMessage(initialData.message || '');
      setAmount(initialData.amount?.toString() || '1');
      setSelectedUnit(initialData.unit ? { label: '', value: initialData.unit } : null);
      setSelectedProduct(
        initialData.selectedProductId
          ? ({
              product_id: initialData.selectedProductId,
              name: initialData.selectedProductName || '',
              barcode: initialData.selectedProductBarcode || null,
              article_number: initialData.selectedProductArticle || null,
            } as StockItem)
          : null,
      );
      setReferenceProduct(
        initialData.referenceProductId
          ? ({
              product_id: initialData.referenceProductId,
              name: initialData.referenceProductName || '',
              barcode: initialData.referenceProductBarcode || null,
              article_number: initialData.referenceProductArticle || null,
            } as StockItem)
          : null,
      );
    }
  }, [isOpen]);

  const { data: actions } = useWorkflowsActionsQuery();
  const workflowState = useSelector(getWorkflows);
  const { selectedTrigger } = workflowState;
  const defaultRecipient = supportsRecordContact(selectedTrigger?.model) ? RECORD_CONTACT_RECIPIENT : '';
  const effectiveRecipient = recipient || defaultRecipient;
  const hasValidModel = !!(selectedTrigger?.model && selectedTrigger.model.trim() !== '');
  const isSaleOrderTrigger = selectedTrigger?.model === 'sale.order';
  const visibleActions = actions?.filter(
    action => isSaleOrderTrigger || !['Produkt beilegen', 'Enclose product'].includes(action.action_type?.trim()),
  );
  const selectedActionTrimmed = selectedAction?.trim() ?? '';
  const templateScope =
    selectedActionTrimmed === 'Case Erstellen' || selectedActionTrimmed === 'Create Case' ? 'case' : undefined;
  const { data: templatesData } = useWorkflowsTemplatesQuery(
    selectedTrigger?.model || '',
    hasValidModel,
    templateScope,
  );
  const variables = templatesData?.map(item => ({ variableName: item.name, variableLabel: item.name })) || [];
  const applyTemplate = (name: string, setContent: (value: string) => void) => {
    const template = templatesData?.find(item => item.name === name);
    if (template) {
      setContent(template.body);
    }
  };

  const { data: companyUsersData } = usePagedUserQuery({ page: 1, size: 100 });
  const { data: profile } = useProfileQuery();
  const notificationRecipientOptions: Option[] = (() => {
    const byId = new Map<number, string>();
    if (profile?.id) {
      byId.set(profile.id, profile.name || profile.email || `User ${profile.id}`);
    }
    (companyUsersData?.items || []).forEach(u => {
      if (u.id) byId.set(u.id, u.name || u.email || `User ${u.id}`);
    });
    return Array.from(byId.entries()).map(([id, name]) => ({ label: name, value: String(id) }));
  })();

  const {
    stockInventory: { items: inventoryItems },
    getStockInventory,
  } = useStockInventory();

  const unitOptions: Option[] = [
    { label: t('workflows.per_order'), value: 'order' },
    { label: t('workflows.per_product'), value: 'product' },
  ];

  const emailRecipientOptions: Option[] = (() => {
    const byEmail = new Map<string, string>();
    if (supportsRecordContact(selectedTrigger?.model)) {
      byEmail.set(RECORD_CONTACT_RECIPIENT, recordContactLabel(selectedTrigger?.model, t));
    }
    if (profile?.email) {
      byEmail.set(profile.email, `${profile.name || profile.email} (${profile.email})`);
    }
    (companyUsersData?.items || []).forEach(u => {
      if (u.email) byEmail.set(u.email, `${u.name || u.email} (${u.email})`);
    });
    return Array.from(byEmail.entries()).map(([email, label]) => ({ label, value: email }));
  })();

  const resetForm = () => {
    setSelectedAction(null);
    setNote('');
    setRecipient('');
    setSubject('');
    setEmailContent('');
    setSelectedPartner(null);
    setNotificationMessage('');
    setCaseTitle('');
    setCaseMessage('');
    setSelectedProduct(null);
    setShowProductPicker(false);
    setProductSearchQuery('');
    setAmount('1');
    setSelectedUnit(null);
    setReferenceProduct(null);
    setShowReferencePicker(false);
    setReferenceSearchQuery('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const actionTrimmed = selectedAction?.trim() ?? '';
  const isProductActionSelected = actionTrimmed === 'Produkt beilegen' || actionTrimmed === 'Enclose product';
  const isEmailActionSelected = actionTrimmed === 'Sende E-Mail' || actionTrimmed === 'Send Mail';
  const isNotificationActionSelected =
    actionTrimmed === 'Sende Benachrichtigung' || actionTrimmed === 'Send Notification';
  const isCaseActionSelected = actionTrimmed === 'Case Erstellen' || actionTrimmed === 'Create Case';
  const parsedAmount = parseInt(amount, 10);
  const resolveProductId = (p: StockItem | null): number | undefined => {
    if (!p) return undefined;
    const anyP = p as any;
    const candidate = anyP.product_id ?? anyP.id;
    return typeof candidate === 'number' && candidate > 0 ? candidate : undefined;
  };
  const selectedProductId = resolveProductId(selectedProduct);
  const isProductFormValid = selectedProductId !== undefined && Number.isFinite(parsedAmount) && parsedAmount >= 1;

  const stripHtml = (html: string) =>
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  const recipientTrimmed = effectiveRecipient.trim();
  const isRecipientValid = recipientTrimmed === RECORD_CONTACT_RECIPIENT || validateEmail(recipientTrimmed);
  const isSubjectValid = subject.trim().length > 0;
  const isEmailContentValid = stripHtml(emailContent).length > 0;
  const isEmailFormValid = isRecipientValid && isSubjectValid && isEmailContentValid;

  const isNotificationFormValid = !!selectedPartner?.id && stripHtml(notificationMessage).length > 0;

  const isCaseFormValid = caseTitle.trim().length > 0 && stripHtml(caseMessage).length > 0;

  const isAddDisabled =
    !selectedAction ||
    (isProductActionSelected && !isProductFormValid) ||
    (isEmailActionSelected && !isEmailFormValid) ||
    (isNotificationActionSelected && !isNotificationFormValid) ||
    (isCaseActionSelected && !isCaseFormValid);

  const handleAdd = () => {
    if (!selectedAction) return;
    if (isProductActionSelected && !isProductFormValid) return;
    if (isEmailActionSelected && !isEmailFormValid) return;
    if (isNotificationActionSelected && !isNotificationFormValid) return;
    if (isCaseActionSelected && !isCaseFormValid) return;

    const formData: ActionFormData = {
      actionType: selectedAction.trim(),
      note,
    };

    if (actionTrimmed === 'Sende E-Mail' || actionTrimmed === 'Send Mail') {
      formData.recipient = effectiveRecipient;
      formData.subject = subject;
      formData.content = emailContent;
    } else if (actionTrimmed === 'Sende Benachrichtigung' || actionTrimmed === 'Send Notification') {
      formData.partnerId = selectedPartner?.id;
      formData.partnerName = selectedPartner?.name;
      formData.message = notificationMessage;
    } else if (actionTrimmed === 'Case Erstellen' || actionTrimmed === 'Create Case') {
      formData.title = caseTitle;
      formData.message = caseMessage;
    } else if (isProductActionSelected) {
      if (selectedProductId === undefined) return;
      formData.selectedProductId = selectedProductId;
      formData.selectedProductName = selectedProduct?.name;
      formData.selectedProductBarcode = selectedProduct?.barcode ?? undefined;
      formData.selectedProductArticle = selectedProduct?.article_number ?? undefined;
      formData.amount = parsedAmount;
      formData.unit = (selectedUnit?.value as string) || (unitOptions[0].value as string);
      formData.referenceProductId = resolveProductId(referenceProduct);
      formData.referenceProductName = referenceProduct?.name;
      formData.referenceProductBarcode = referenceProduct?.barcode ?? undefined;
      formData.referenceProductArticle = referenceProduct?.article_number ?? undefined;
    }

    onActionSelect(formData);
    resetForm();
    onClose();
  };

  const handleActionClick = (actionType: string) => {
    if (selectedAction === actionType) return;
    setSelectedAction(actionType);
  };

  const isEmailAction = actionTrimmed === 'Sende E-Mail' || actionTrimmed === 'Send Mail';
  const isNotificationAction = actionTrimmed === 'Sende Benachrichtigung' || actionTrimmed === 'Send Notification';
  const isCaseAction = actionTrimmed === 'Case Erstellen' || actionTrimmed === 'Create Case';
  const isProductAction = isProductActionSelected;

  const renderEmailForm = () => (
    <div className={styles.formSection}>
      <Divider customColor="border-subtle-transparent" className="mb-16" />
      <div className={styles.fieldGroup}>
        <span className={styles.fieldLabel}>{t('workflows.action_overlay.recipient')}</span>
        <Select
          options={emailRecipientOptions}
          placeholder={t('general.select')}
          isSearchable
          onChange={option => setRecipient(option?.value ?? '')}
          defaultValue={emailRecipientOptions.find(o => o.value === effectiveRecipient)}
        />
      </div>
      <div className={styles.fieldGroup}>
        <span className={styles.fieldLabel}>{t('workflows.subject')}</span>
        <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="" className="w-full" />
      </div>
      <div className={styles.fieldGroup}>
        <span className={styles.fieldLabel}>{t('workflows.action_overlay.content')}</span>
        <TextAreaMenu
          value={emailContent}
          onChange={e => setEmailContent(e.target.innerHTML)}
          variables={variables}
          onSelect={name => applyTemplate(name, setEmailContent)}
          insertButtonTitle={t('workflows.action_overlay.insert_template')}
        />
      </div>
    </div>
  );

  const renderNotificationForm = () => (
    <div className={styles.formSection}>
      <Divider customColor="border-subtle-transparent" className="mb-16" />
      <div className={styles.fieldGroup}>
        <span className={styles.fieldLabel}>{t('workflows.action_overlay.recipient')}</span>
        <Select
          options={notificationRecipientOptions}
          placeholder={t('general.select')}
          isSearchable
          onChange={option => {
            setSelectedPartner(option ? { id: Number(option.value), name: option.label } : null);
          }}
          defaultValue={
            selectedPartner ? { label: selectedPartner.name, value: String(selectedPartner.id) } : undefined
          }
        />
      </div>
      <div className={styles.fieldGroup}>
        <span className={styles.fieldLabel}>{t('workflows.message')}</span>
        <TextAreaMenu
          value={notificationMessage}
          onChange={e => setNotificationMessage(e.target.innerHTML)}
          variables={variables}
          onSelect={name => applyTemplate(name, setNotificationMessage)}
          insertButtonTitle={t('workflows.action_overlay.insert_template')}
        />
      </div>
    </div>
  );

  const renderCaseForm = () => (
    <div className={styles.formSection}>
      <Divider customColor="border-subtle-transparent" className="mb-16" />
      <div className={styles.fieldGroup}>
        <span className={styles.fieldLabel}>{t('workflows.title')}</span>
        <Input value={caseTitle} onChange={e => setCaseTitle(e.target.value)} placeholder="" className="w-full" />
      </div>
      <div className={styles.fieldGroup}>
        <span className={styles.fieldLabel}>{t('workflows.message')}</span>
        {/* Case editor offers only case-suited templates (scope filter on
            the templates query above). */}
        <TextAreaMenu
          value={caseMessage}
          onChange={e => setCaseMessage(e.target.innerHTML)}
          variables={variables}
          onSelect={name => applyTemplate(name, setCaseMessage)}
          insertButtonTitle={t('workflows.action_overlay.insert_template')}
        />
      </div>
    </div>
  );

  const handleProductSearch = (query: string) => {
    setProductSearchQuery(query);
    getStockInventory(
      1,
      50,
      query
        ? [
            { key: 'name', value: query, operator: 'ilike' },
            { key: 'barcode', value: query, operator: 'ilike' },
            { key: 'default_code', value: query, operator: 'ilike' },
          ]
        : undefined,
    );
  };

  const handleReferenceSearch = (query: string) => {
    setReferenceSearchQuery(query);
    getStockInventory(
      1,
      50,
      query
        ? [
            { key: 'name', value: query, operator: 'ilike' },
            { key: 'barcode', value: query, operator: 'ilike' },
            { key: 'default_code', value: query, operator: 'ilike' },
          ]
        : undefined,
    );
  };

  const renderInlineProductPicker = (
    label: string,
    product: StockItem | null,
    showPicker: boolean,
    setShowPicker: (v: boolean) => void,
    searchQuery: string,
    onSearchChange: (q: string) => void,
    onSelect: (p: StockItem) => void,
    onClear: () => void,
  ) => (
    <div className={styles.fieldGroup}>
      <span className={styles.fieldLabel}>{label}</span>
      <button
        type="button"
        className={styles.productDropdownTrigger}
        onClick={() => {
          setShowPicker(!showPicker);
          if (!showPicker) onSearchChange('');
        }}>
        <span className="typo-text-14 text-faded">{t('general.select')}</span>
        <Icon name="chevronDown" width={16} height={16} className="text-faded" />
      </button>
      {showPicker && (
        <div className={styles.inlineProductPicker}>
          <Input
            value={searchQuery}
            clearable
            className="mb-8"
            onChange={e => onSearchChange(e.target.value)}
            placeholder={t('general.search_by_barcode_or_name')}
          />
          <div className={styles.productsGrid}>
            {inventoryItems.length === 0 && (
              <div className="typo-text-12 text-faded col-span-2">{t('general.no_products_found')}</div>
            )}
            {inventoryItems.map(item => {
              const itemId = (item as any).product_id ?? (item as any).id;
              const productId = product ? (product as any).product_id ?? (product as any).id : undefined;
              const isSelected = productId === itemId;
              return (
                <div
                  key={item.product_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    onSelect(item);
                    setShowPicker(false);
                    onSearchChange('');
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      onSelect(item);
                      setShowPicker(false);
                      onSearchChange('');
                    }
                  }}
                  className={classNames(styles.productGridItem, { [styles.isSelected]: isSelected })}>
                  <Checkbox checked={isSelected} disabled />
                  <div className="flex flex-col gap-4">
                    <span className="typo-text-12--bold">{item.name}</span>
                    <span className="typo-text-12 text-faded">
                      {item.article_number || ''} | {item.barcode || ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {product && (
        <div className={styles.selectedProductCard}>
          <div className="flex flex-col">
            <span className="typo-text-14--bold">{product.name}</span>
            <span className="typo-text-12 text-faded">
              {product.article_number || ''} | {product.barcode || ''}
            </span>
          </div>
          <Button tone={ButtonToneProps.ERROR} icon="delete" onClick={onClear} />
        </div>
      )}
    </div>
  );

  const renderProductForm = () => (
    <div className={styles.formSection}>
      <Divider customColor="border-subtle-transparent" className="mb-16" />

      {renderInlineProductPicker(
        t('workflows.action_overlay.products_to_add'),
        selectedProduct,
        showProductPicker,
        setShowProductPicker,
        productSearchQuery,
        handleProductSearch,
        p => setSelectedProduct(p),
        () => setSelectedProduct(null),
      )}

      <div className={styles.fieldGroup}>
        <div className={styles.amountRow}>
          <div className={styles.amountInput}>
            <span className={styles.fieldLabel}>{t('workflows.action_overlay.quantity')}</span>
            <Input
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="1"
              className="w-full"
              type="number"
            />
          </div>
          <div className={styles.unitSelect}>
            <Select
              options={unitOptions}
              placeholder={t('general.select')}
              onChange={option => setSelectedUnit(option)}
              defaultValue={selectedUnit || unitOptions[0]}
            />
          </div>
        </div>
      </div>

      {renderInlineProductPicker(
        t('workflows.action_overlay.reference_product'),
        referenceProduct,
        showReferencePicker,
        setShowReferencePicker,
        referenceSearchQuery,
        handleReferenceSearch,
        p => setReferenceProduct(p),
        () => setReferenceProduct(null),
      )}
      <p className={styles.referenceHint}>{t('workflows.action_overlay.reference_product_hint')}</p>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} hasCloseButton>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className="typo-title-24">{t('workflows.action_overlay.title')}</h2>
          <p className="typo-text-14 text-faded mt-4">{t('workflows.action_overlay.subtitle')}</p>
        </div>

        <div className={styles.body}>
          <div className={styles.noteSection}>
            <span className={styles.noteLabel}>{t('workflows.action_overlay.note')}</span>
            <Input
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={t('workflows.action_overlay.note_placeholder')}
              className="w-full"
            />
          </div>

          <Divider customColor="border-subtle-transparent" className={styles.fullWidthDivider} />

          <div className={styles.actionList}>
            <span className={styles.actionsLabel}>{t('workflows.actions')}</span>
            {visibleActions?.map(action => (
              <button
                type="button"
                key={action.action_type}
                className={classNames(styles.actionItem, {
                  [styles.selected]: selectedAction === action.action_type,
                })}
                onClick={() => handleActionClick(action.action_type)}>
                <div className={styles.actionIcon}>
                  <Icon name={getActionIcon(action.action_type)} width={20} height={20} className="text-white" />
                </div>
                <span className="typo-text-14 text-full pl-8">{action.action_type}</span>
              </button>
            ))}
          </div>

          {isEmailAction && renderEmailForm()}
          {isNotificationAction && renderNotificationForm()}
          {isCaseAction && renderCaseForm()}
          {isProductAction && renderProductForm()}
        </div>

        <div className={styles.footer}>
          <Button tone={ButtonToneProps.SECONDARY} title={t('general.cancel')} onClick={handleClose} />
          <Button
            tone={ButtonToneProps.PRIMARY_BLUE}
            title={t('workflows.action_overlay.add')}
            onClick={handleAdd}
            disabled={isAddDisabled}
          />
        </div>
      </div>
    </Modal>
  );
}
